import { LocalStorageProvider } from './providers/LocalStorageProvider';
import { MemoryProvider } from './providers/MemoryProvider';
import { ApiProvider } from './providers/ApiProvider';
import { logEvent, throwError } from '../../utils/env';
import { get, each, set, toLower } from 'lodash';

export const ACTIVE_MODULE = 'activeModule';

export class UserStoreService {

  static LOCAL_STORAGE_PROVIDER = 'LOCAL_STORAGE_PROVIDER';
  static MEMORY_PROVIDER = 'MEMORY_PROVIDER';
  static API_PROVIDER = 'API_PROVIDER';

  static DEFAULT_PROVIDER = 'API_PROVIDER';

  /** @type {UserStoreService | null} */
  static instance = null;

  static getInstance(userId, providerType = UserStoreService.DEFAULT_PROVIDER) {
    if (!UserStoreService.instance) {
      UserStoreService.invalidate(userId, providerType);
    }

    return UserStoreService.instance;
  }

  static invalidate(userId, isActAsAction = false, providerType = UserStoreService.DEFAULT_PROVIDER) {
    UserStoreService.instance = new UserStoreService(userId, isActAsAction, providerType);
  }

  static resolveProvider(userId, providerType) {
    switch (providerType) {
      case UserStoreService.LOCAL_STORAGE_PROVIDER:
        try {
          return new LocalStorageProvider(userId);
        } catch (e) {
          logEvent(e);
          return new MemoryProvider(userId);
        }
      case UserStoreService.MEMORY_PROVIDER: return new MemoryProvider(userId);
      case UserStoreService.API_PROVIDER: return new ApiProvider(userId);
      default:
        throwError(`UserStoreService: no provider for ${providerType}`);
    }
  }

  provider;

  userId;

  isActAsAction;

  constructor(userId = 0, isActAsAction = false, providerType = UserStoreService.MEMORY_PROVIDER) {
    this.userId = userId;
    this.isActAsAction = isActAsAction;
    this.provider = UserStoreService.resolveProvider(userId, providerType);
  }

  async get(key, defaultValue = null) {
    const response = await this.provider.get(this.getKey(key), this.toDataObject(defaultValue));
    return this.fromDataObject(response);
  }

  getSync(key, defaultValue = null) {
    const response = this.provider.getSync(this.getKey(key), this.toDataObject(defaultValue));
    return this.fromDataObject(response);
  }

  async patch(key, value) {
    if (this.isActAsAction) {
      return true;
    }

    if (JSON.stringify(this.getSync(this.getKey(key))) === JSON.stringify(value)) {
      return true;
    }

    return this.provider.patch(this.getKey(key), this.toDataObject(value));
  }

  async patchObject(keyValuePairs) {
    if (this.isActAsAction) {
      return true;
    }

    const _data = {};

    each(keyValuePairs, (value, key) => {
      set(_data, key, this.toDataObject(value));
    });

    return this.provider.patchObject(_data);
  }

  toDataObject(value) {
    return { value };
  }

  fromDataObject(object) {
    return get(object, 'value');
  }

  getKey(key) {
    return toLower(key);
  }
}
